import React, { useState, useEffect } from 'react';
import './VisitorCenter.css';

const VisitorCenter = () => {
  const [visitorType, setVisitorType] = useState(null);
  const [selectedConference, setSelectedConference] = useState(null);
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);

  const visitorTypes = [
    { id: 'conference', label: 'Conference Delegate', icon: '👔', description: 'Here for a conference or event' },
    { id: 'business', label: 'Business Traveler', icon: '💼', description: 'Business meetings or work trip' },
    { id: 'family', label: 'Family Visit', icon: '👨‍👩‍👧‍👦', description: 'Visiting with family or kids' },
    { id: 'solo', label: 'Solo Explorer', icon: '🎒', description: 'Exploring on your own' },
    { id: 'romantic', label: 'Romantic Getaway', icon: '💕', description: 'Couples trip or date night' },
    { id: 'local', label: 'I\'m a Local', icon: '🏠', description: 'I live here' }
  ];

  const itineraries = {
    conference: [
      { title: '24 Hours in Fredericton', duration: '1 day', highlights: 'Historic downtown, riverfront, local dining' },
      { title: 'Business Dining Guide', duration: 'Evening', highlights: 'Upscale restaurants, cocktail bars, networking spots' },
      { title: 'Coffee Lover\'s Tour', duration: '2-3 hours', highlights: 'Best local cafes, specialty roasters' },
      { title: 'After-Conference Relaxation', duration: 'Evening', highlights: 'Breweries, distilleries, live music' }
    ],
    business: [
      { title: 'Quick Business Lunch Spots', duration: '1 hour', highlights: 'Fast, professional, WiFi available' },
      { title: 'Client Dinner Venues', duration: 'Evening', highlights: 'Impressive restaurants, private dining' },
      { title: 'Morning Productivity', duration: 'Morning', highlights: 'Coffee shops with workspace, gyms' }
    ],
    family: [
      { title: 'Family Fun Day', duration: 'Full day', highlights: 'Science East, Odell Park, ice cream shops' },
      { title: 'Outdoor Adventures', duration: 'Half day', highlights: 'Trails, parks, splash pads' },
      { title: 'Rainy Day Activities', duration: 'Half day', highlights: 'Museums, indoor play, bowling' }
    ],
    solo: [
      { title: 'Solo Traveler\'s Highlights', duration: '1 day', highlights: 'Walkable downtown, safe areas, social spots' },
      { title: 'Local Art & Culture', duration: 'Half day', highlights: 'Galleries, studios, craft shops' },
      { title: 'Nature Escapes', duration: 'Half day', highlights: 'Trails, river walks, photo spots' }
    ],
    romantic: [
      { title: 'Romantic Evening', duration: 'Evening', highlights: 'Fine dining, river views, live music' },
      { title: 'Couples\' Day Out', duration: 'Full day', highlights: 'Brunch, galleries, sunset spots' }
    ]
  };

  const welcomeMessages = {
    conference: {
      title: 'Welcome, Conference Delegate! 👔',
      message: 'Fredericton is thrilled to host you! Our local businesses and community are excited about your conference and have prepared special experiences just for delegates.',
      benefits: [
        'Show your conference badge for special discounts at local businesses',
        'Safe, walkable downtown - everything within 15 minutes',
        'Smaller city charm with services and amenities you need',
        'Gateway to explore all of Atlantic Canada'
      ]
    },
    business: {
      title: 'Welcome, Business Traveler! 💼',
      message: 'Fredericton combines professional excellence with Maritime hospitality. Find the perfect spots for meetings, client dinners, and downtime.',
      benefits: [
        'Professional dining and meeting venues',
        'Excellent WiFi and business services',
        'Easy navigation and parking',
        'Blend productivity with local experiences'
      ]
    },
    family: {
      title: 'Welcome, Families! 👨‍👩‍👧‍👦',
      message: 'Fredericton is a family-friendly city with safe neighborhoods, great parks, and activities for all ages.',
      benefits: [
        'Safe, walkable city perfect for families',
        'Free outdoor activities and parks',
        'Kid-friendly restaurants and attractions',
        'Educational experiences at Science East and museums'
      ]
    },
    solo: {
      title: 'Welcome, Solo Traveler! 🎒',
      message: 'Fredericton is one of Canada\'s safest cities and perfect for solo exploration. Our friendly locals are always happy to help!',
      benefits: [
        'Safe to explore day and night',
        'Friendly, welcoming community',
        'Walkable downtown core',
        'Great social spots to meet locals'
      ]
    },
    romantic: {
      title: 'Welcome, Couples! 💕',
      message: 'Discover Fredericton\'s romantic side - riverside walks, intimate dining, and charming experiences.',
      benefits: [
        'Romantic riverfront views',
        'Intimate dining experiences',
        'Charming local shops and galleries',
        'Beautiful nature settings'
      ]
    }
  };

  useEffect(() => {
    // Mock fetch conferences - replace with actual API call
    setConferences([
      { id: 1, name: 'Atlantic Healthcare Conference 2025', dates: 'Jan 15-17, 2025', attendees: '300+', venue: 'Fredericton Convention Centre' },
      { id: 2, name: 'Maritime Education Summit', dates: 'Feb 8-10, 2025', attendees: '200+', venue: 'Fredericton Convention Centre' },
      { id: 3, name: 'Canadian Tech Innovation Forum', dates: 'Mar 12-14, 2025', attendees: '400+', venue: 'Fredericton Convention Centre' }
    ]);
    setLoading(false);
  }, []);

  if (!visitorType) {
    return (
      <div className="visitor-center">
        <div style={{ marginBottom: '20px' }}>
          <button className="vc-back-btn" onClick={() => window.location.href = '/webapp/home'}>
            ← Back to Home
          </button>
        </div>
        
        <div className="vc-hero">
          <h1>Welcome to Fredericton! 🍁</h1>
          <p className="vc-subtitle">Let us help you make the most of your visit</p>
        </div>

        <div className="visitor-type-grid">
          {visitorTypes.map(type => (
            <div key={type.id} className="visitor-type-card" onClick={() => setVisitorType(type.id)}>
              <div className="type-icon">{type.icon}</div>
              <h3>{type.label}</h3>
              <p>{type.description}</p>
            </div>
          ))}
        </div>

        <div className="vc-info-section">
          <h2>Why Visit Fredericton?</h2>
          <div className="info-grid">
            <div className="info-card">
              <span className="info-icon">🏛️</span>
              <h4>Rich History & Culture</h4>
              <p>Capital city with 200+ years of history, galleries, and museums</p>
            </div>
            <div className="info-card">
              <span className="info-icon">🚶</span>
              <h4>Safe & Walkable</h4>
              <p>One of Canada's safest cities with a compact, walkable downtown</p>
            </div>
            <div className="info-card">
              <span className="info-icon">🍽️</span>
              <h4>Diverse Dining</h4>
              <p>From fine dining to ethnic cuisine, craft breweries to coffee roasters</p>
            </div>
            <div className="info-card">
              <span className="info-icon">🌲</span>
              <h4>Nature & Outdoors</h4>
              <p>Beautiful trails, parks, and the St. John River right downtown</p>
            </div>
            <div className="info-card">
              <span className="info-icon">🗺️</span>
              <h4>Gateway to Atlantic Canada</h4>
              <p>Perfect base for exploring NB, PEI, NS, and Newfoundland</p>
            </div>
            <div className="info-card">
              <span className="info-icon">💼</span>
              <h4>Conference Ready</h4>
              <p>Modern facilities with all amenities you need, walkable distances</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const welcome = welcomeMessages[visitorType];
  const suggestedItineraries = itineraries[visitorType] || [];

  return (
    <div className="visitor-center">
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button className="vc-back-btn" onClick={() => window.location.href = '/webapp/home'}>
          ← Back to Home
        </button>
        <button className="vc-back-btn" onClick={() => setVisitorType(null)} style={{ background: '#e8eaf6' }}>
          Change Visitor Type
        </button>
      </div>

      <div className="vc-welcome-section">
        <h1>{welcome.title}</h1>
        <p className="welcome-message">{welcome.message}</p>

        <div className="benefits-grid">
          {welcome.benefits.map((benefit, idx) => (
            <div key={idx} className="benefit-card">
              <span className="check-icon">✓</span>
              <p>{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      {visitorType === 'conference' && (
        <div className="conference-section">
          <h2>Are you attending a conference?</h2>
          {loading ? (
            <p>Loading conferences...</p>
          ) : (
            <div className="conference-grid">
              {conferences.map(conf => (
                <div 
                  key={conf.id} 
                  className={`conference-card ${selectedConference?.id === conf.id ? 'selected' : ''}`}
                  onClick={() => setSelectedConference(conf)}
                >
                  <h4>{conf.name}</h4>
                  <p className="conf-dates">📅 {conf.dates}</p>
                  <p className="conf-attendees">👥 {conf.attendees} expected</p>
                  <p className="conf-venue">📍 {conf.venue}</p>
                </div>
              ))}
            </div>
          )}
          
          {selectedConference && (
            <div className="conf-benefits">
              <h3>Special Perks for {selectedConference.name} Delegates:</h3>
              <ul>
                <li>🏷️ Show your badge for 10-20% off at participating local businesses</li>
                <li>🍽️ Exclusive delegate dining specials at downtown restaurants</li>
                <li>☕ Free coffee upgrades at select cafes</li>
                <li>🎟️ Discounted tickets to local attractions and events</li>
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="itineraries-section">
        <h2>Suggested Itineraries for You</h2>
        <div className="itinerary-grid">
          {suggestedItineraries.map((itinerary, idx) => (
            <div key={idx} className="itinerary-card">
              <h3>{itinerary.title}</h3>
              <p className="itinerary-duration">⏱️ {itinerary.duration}</p>
              <p className="itinerary-highlights">{itinerary.highlights}</p>
              <button className="btn-view-itinerary">View Details →</button>
            </div>
          ))}
        </div>
      </div>

      <div className="quick-links-section">
        <h2>Quick Access</h2>
        <div className="quick-links-grid">
          <a href="/webapp/home" className="quick-link">
            <span className="ql-icon">🏠</span>
            <span>Explore Events</span>
          </a>
          <a href="/webapp/home" className="quick-link">
            <span className="ql-icon">🍽️</span>
            <span>Find Restaurants</span>
          </a>
          <a href="/webapp/home" className="quick-link">
            <span className="ql-icon">☕</span>
            <span>Coffee Shops</span>
          </a>
          <a href="/webapp/home" className="quick-link">
            <span className="ql-icon">🎭</span>
            <span>Arts & Culture</span>
          </a>
          <a href="/webapp/home" className="quick-link">
            <span className="ql-icon">🏨</span>
            <span>Hotels</span>
          </a>
          <a href="/webapp/home" className="quick-link">
            <span className="ql-icon">🚶</span>
            <span>Walking Tours</span>
          </a>
        </div>
      </div>

      <div className="safety-info">
        <h3>🛡️ Safety & Getting Around</h3>
        <div className="safety-grid">
          <div className="safety-card">
            <h4>Walkability</h4>
            <p>Downtown core is 100% walkable. Convention Centre to most restaurants: 5-15 min walk.</p>
          </div>
          <div className="safety-card">
            <h4>Safety</h4>
            <p>One of Canada's safest cities. Safe to walk day and night in downtown area.</p>
          </div>
          <div className="safety-card">
            <h4>Weather</h4>
            <p>Check current conditions. We'll suggest indoor/outdoor activities accordingly.</p>
          </div>
          <div className="safety-card">
            <h4>Transit</h4>
            <p>Fredericton Transit available. Most attractions walkable or short drive/cab ride.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorCenter;
